//@flow
import React, {useState, useEffect, useMemo} from 'react';
import {
  formatStoreDeviceIdentifier,
  useStoreDevices,
} from '../../../../../../utils';
import {makeStyles, Divider, ButtonBase, MenuItem} from '@material-ui/core';
import {Warning, WhatsApp, ArrowForward, Close} from '@material-ui/icons';
import {colors} from '../../../../../../../../../assets/styles/colors';
import {useForm} from 'react-hook-form';
import {useHistory} from 'react-router-dom';
import CustomTextField from '../../../../../../../../../elements/CustomTextField/CustomTextField';
import CustomButton from '../../../../../../../../../elements/CustomButton/CustomButton';
import CustomSelect from '../../../../../../../../../elements/CustomSelect/CustomSelect';
import {openConversationService} from '../../../../../../promises/conversation';
import {
  useConversationIdSelected,
  useCurrentConversation,
  useCurrentConversationState,
  useFilterConversationsActions,
} from '../../../../../../utils/conversation';
import {useAlert} from '../../../../../../../../../elements/Alert/AlertContext';
import {formatPhoneNumber} from '../../../../../../../../../utils/phone';

const useStyles = makeStyles(theme => ({
  root: {
    width: '100%',
    position: 'absolute',
    bottom: '0.5rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  label: {
    paddingInline: '.5rem',
    fontWeight: 600,
  },
  labelFunnel: {
    paddingInline: '.5rem',
    fontWeight: 600,
    width: '20rem',
  },
  description: {
    paddingInline: '.5rem',
    fontSize: '0.8rem',
    color: colors.grey600,
    textAlign: 'left',
  },
  descriptionLink: {
    color: colors.grey700,
    fontWeight: 600,
  },
  notificationButton: {
    padding: '.5rem',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    transition: 'transform 0.2s ease-in-out, boxShadow 0.2s ease-in-out',
    background: `#fef5e1`,
    borderRadius: '1rem',
    width: '100%',
  },
  notificationHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
  },
  notificationHeaderInfo: {
    display: 'flex',
    alignItems: 'center',
  },
  notificationHeaderFunnel: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    width: '100%',
  },
  notificationHeaderInfoFunnel: {
    display: 'flex',
    alignItems: 'flex-start',
  },
  iconClose: {
    color: colors.grey700,
    borderRadius: '50%',
  },
  count: {
    width: '24px',
    height: '24px',
    fontWeight: 600,
    borderRadius: '50%',
    color: 'white',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',

    '&[data-theme="primary"]': {
      background: colors.primary,
      color: 'white',
    },
    '&[data-theme="secondary"]': {
      background: colors.secondary,
      color: 'white',
    },
    '&[data-theme="success"]': {
      background: colors.success,
      color: 'white',
    },
    '&[data-theme="warning"]': {
      background: colors.warning,
      color: 'white',
    },
    '&[data-theme="info"]': {
      background: colors.info,
      color: 'white',
    },
    '&[data-theme="danger"]': {
      background: colors.danger,
      color: 'white',
    },
  },
  icon: {
    width: '24px',
    height: '24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '1.35rem',
  },
  closeButton: {
    width: '20px',
    height: '20px',
    color: '#fbaf19',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  form: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: '.5rem',
  },
  textFieldCustom: {
    '& input': {
      fontSize: '1rem',
    },
  },

  textFieldCustomAdornment: {
    display: 'flex',
    alignItems: 'center',
    gap: '.5rem',

    '& span': {
      fontSize: '1rem',
      color: colors.grey700,
    },
  },
  iconWhatsapp: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#25d366',
    color: 'white',
    borderRadius: '50%',
    width: '28px',
    height: '28px',
    padding: '0.5rem',

    '& svg': {
      width: '1.2rem',
      height: '1.2rem',
    },
  },
  divider: {
    background: '#fde0a5',
  },
  button: {
    border: '1px solid transparent',
    color: '#55556d',

    '&:hover': {
      border: '1px solid transparent',
    },
  },
  iconButtonForward: {
    marginLeft: '0.5rem',
  },
  notificationInitConversation: {
    display: 'flex',
    flexDirection: 'column',
    gap: '.5rem',
  },
  groupButtons: {
    display: 'flex',
    gap: '.5rem',
    alignItems: 'center',
    justifyContent: 'center',
  },
  buttonInit: {
    color: colors.primary,
    cursor: 'pointer',
    fontWeight: 600,
    paddingLeft: '0.2rem',
    fontSize: '0.85rem',
  },
  buttonClose: {
    background: colors.grey300,
    color: colors.grey700,
    padding: '.5rem',
    borderRadius: '1rem',
    width: '6rem',
    textAlign: 'center',
    cursor: 'pointer',
  },
}));

function getErrorMessage(error: string) {
  if (error === 'CrmMessagingContactAlreadyExistError') {
    return 'El contacto ya existe';
  }

  return 'Ocurrió un error';
}

type Props = {
  nextConversationId: string | null,
  showInitConversation: boolean,
};

export default function ChangePhoneMessageNotification(props: Props) {
  const {nextConversationId, showInitConversation} = props;
  const {filterByTags, filterByState} = useFilterConversationsActions();
  const {storeDevices} = useStoreDevices({
    refetchOnMount: true,
  });

  const {currentConversation: conversation} = useCurrentConversation();
  const {setConversationId} = useConversationIdSelected();
  const {toggleConversationState} = useCurrentConversationState();

  const classes = useStyles();
  const history = useHistory();
  const {register, handleSubmit} = useForm();
  const {showAlert} = useAlert();
  const [loading, setLoading] = useState(false);
  const [deviceSelectedId, setDeviceSelectedId] = useState('');

  const [show, setShow] = useState(true);

  const connectedWhatsappDevices = useMemo(
    () =>
      storeDevices.filter(
        device =>
          device.service === 'WHATSAPP_WEB' && device.state === 'CONNECTED'
      ),
    [storeDevices]
  );

  const whatsappDevice = connectedWhatsappDevices.find(
    device => device.id === deviceSelectedId
  );

  useEffect(() => {
    if (connectedWhatsappDevices.length === 0) {
      setDeviceSelectedId('');
      return;
    }

    const stillValid = connectedWhatsappDevices.some(
      device => device.id === deviceSelectedId
    );
    if (stillValid) return;

    if (connectedWhatsappDevices.length === 1) {
      setDeviceSelectedId(connectedWhatsappDevices[0].id);
      return;
    }

    setDeviceSelectedId('');
  }, [connectedWhatsappDevices, deviceSelectedId]);

  const handleNewConversation = data => {
    if (!whatsappDevice) {
      showAlert('Selecciona un dispositivo', {
        type: 'warning',
      });
      return;
    }
    if (!conversation) return;
    if (!data.message) {
      showAlert('El mensaje es requerido', {
        type: 'warning',
      });
      return;
    }

    setLoading(true);

    const fullName =
      conversation?.contactInformation.pushName ||
      formatPhoneNumber(`+${conversation.contactInformation.phone}`);

    if (conversation.crmConversationState === 'OPEN') {
      toggleConversationState();
    }

    openConversationService({
      contactInformation: {
        fullName: fullName,
      },
      message: {
        text: data['message'],
      },
      deviceId: whatsappDevice.id,
      identifier: conversation?.contactInformation.phone,
      provider: 'WHATSAPP_WEB',
      previousConversationId: conversation.id,
    })
      .then(() => {
        filterByTags([]);
        setLoading(false);
      })
      .catch(error => {
        console.log(error);
        const message = getErrorMessage(error?.error?.name);
        showAlert(message, {
          type: 'error',
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleCloseClick = () => {
    setShow(false);
  };

  useEffect(() => {
    setShow(true);
  }, [conversation]);

  if (!show) return null;

  return (
    <aside className={classes.root}>
      <div className={classes.notificationButton} data-theme={'warning'}>
        {showInitConversation ? (
          <div className={classes.notificationHeader}>
            <div className={classes.notificationHeaderInfo}>
              <div className={classes.closeButton}>
                <Warning fontSize="small" />
              </div>

              <span className={classes.label} data-theme={'warning'}>
                {nextConversationId
                  ? 'Ya se ha iniciado una conversación con el nuevo número'
                  : 'Cambio de número'}
              </span>
            </div>
            <ButtonBase
              className={classes.iconClose}
              onClick={e => {
                e.stopPropagation();
                handleCloseClick();
              }}
            >
              <Close />
            </ButtonBase>
          </div>
        ) : (
          <div className={classes.notificationHeaderFunnel}>
            <div className={classes.notificationHeaderInfoFunnel}>
              <div className={classes.closeButton}>
                <Warning fontSize="small" />
              </div>
              <div className={classes.notificationInitConversation}>
                <span className={classes.labelFunnel} data-theme={'warning'}>
                  Esta conversación ha sido archivado. ¿Desea iniciar una nueva
                  conversación?
                  <ButtonBase
                    className={classes.buttonInit}
                    onClick={e => {
                      e.stopPropagation();
                      setConversationId(conversation?.id || '');
                      filterByState('OPEN');
                      history.push('/chat/multi-chat');
                    }}
                  >
                    Click aquí
                  </ButtonBase>
                </span>
              </div>
            </div>
            <ButtonBase
              className={classes.iconClose}
              onClick={e => {
                e.stopPropagation();
                handleCloseClick();
              }}
            >
              <Close />
            </ButtonBase>
          </div>
        )}

        {!nextConversationId && showInitConversation && (
          <>
            <p className={classes.description}>
              El número de WhatsApp con el que iniciaste la conversación ha
              cambiado. Para reconectar el nuevo número seleccionar aquí. <br />
            </p>

            <form
              className={classes.form}
              onSubmit={handleSubmit(handleNewConversation)}
            >
              {connectedWhatsappDevices.length > 1 ? (
                <CustomSelect
                  fullWidth
                  value={deviceSelectedId}
                  onChange={e => setDeviceSelectedId(e.target.value)}
                  label="Dispositivo"
                >
                  {connectedWhatsappDevices.map(device => (
                    <MenuItem key={device.id} value={device.id}>
                      {formatStoreDeviceIdentifier(device)}
                    </MenuItem>
                  ))}
                </CustomSelect>
              ) : (
                <CustomTextField
                  className={classes.textFieldCustom}
                  type="text"
                  fullWidth
                  name={'phone'}
                  value={
                    whatsappDevice
                      ? formatPhoneNumber(`+${whatsappDevice?.identifier}`)
                      : ''
                  }
                  InputProps={{
                    readOnly: true,
                    startAdornment: (
                      <div className={classes.textFieldCustomAdornment}>
                        <div className={classes.iconWhatsapp}>
                          <WhatsApp />
                        </div>
                        <span>WhatsApp:</span>
                      </div>
                    ),
                  }}
                />
              )}
              <CustomTextField
                placeholder="Envía un mensaje"
                name={'message'}
                inputRef={register}
                multiline
                rowsMax={4}
                rows={4}
              />
              <Divider className={classes.divider} />
              <CustomButton
                variant="outlined"
                fullWidth
                className={classes.button}
                type="submit"
                disabled={loading || !whatsappDevice}
              >
                Iniciar conversación{' '}
                <ArrowForward className={classes.iconButtonForward} />
              </CustomButton>
            </form>
          </>
        )}
      </div>
    </aside>
  );
}
